import { useNavigate, useParams } from "react-router-dom";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { DataTable } from "mantine-datatable";
import {
  ActionIcon,
  Anchor,
  Badge,
  MantineColor,
  Tooltip,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { StepType, TournamentStep } from "../../../../../gql/graphql";
import { ListPageHeader } from "../../../components/PageHeader";
import { StatusBadge } from "../../../components/StatusBadge";
import { useConfirmModal } from "../../../components/ConfirmModal";
import { ListPageSkeleton } from "../../../components/SkeletonLoader";

const STEP_TYPE_COLORS: Record<StepType, MantineColor> = {
  [StepType.Score]: "blue",
  [StepType.RoundRobin]: "orange",
  [StepType.SingleElim]: "red",
  [StepType.DoubleElim]: "pink",
  [StepType.Custom]: "gray",
};

export const StepList = () => {
  const navigate = useNavigate();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { confirm, modal: confirmModal } = useConfirmModal();

  const { data, loading, refetch } = useQuery<{
    tournamentSteps: TournamentStep[];
  }>(
    gql`
      query ($tournamentId: ID!) {
        tournamentSteps(tournamentId: $tournamentId) {
          id
          name
          order
          status
          type
          tournamentId
          configuration {
            groups {
              rounds {
                name
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        tournamentId,
      },
      skip: !tournamentId,
    },
  );

  const [deleteTournamentStep] = useMutation<
    { deleteTournamentStep: boolean },
    { id: string }
  >(
    gql`
      mutation ($id: ID!) {
        deleteTournamentStep(stepId: $id)
      }
    `,
    {
      onCompleted: () => {
        refetch();
      },
    },
  );

  const handleDelete = async (item: TournamentStep) => {
    const confirmed = await confirm({
      title: "Delete step",
      message: "Are you sure you want to delete this step?",
      confirmLabel: "Delete",
      type: "danger",
    });

    if (confirmed) {
      await deleteTournamentStep({ variables: { id: item.id } });
    }
  };

  if (loading) {
    return <ListPageSkeleton />;
  }

  return (
    <div className="wp-fade-in">
      {confirmModal}

      <ListPageHeader
        title="Steps"
        createLabel="Create Step"
        onCreate={() => navigate("new")}
        showBreadcrumbs={false}
      />

      <DataTable
        columns={[
          {
            accessor: "name",
            title: "Name",
            render: (item) => (
              <Anchor onClick={() => navigate(`${item.id}`)}>
                {item.name}
              </Anchor>
            ),
          },
          { accessor: "order", title: "Order" },
          {
            accessor: "status",
            title: "Status",
            render: (item) => <StatusBadge status={item.status} />,
          },
          {
            accessor: "type",
            title: "Type",
            render: (item) => (
              <Badge color={STEP_TYPE_COLORS[item.type] ?? "gray"}>
                {item.type}
              </Badge>
            ),
          },
          {
            accessor: "",
            title: "",
            render: (item: TournamentStep) => (
              <Tooltip label="Delete item">
                <ActionIcon color="red" onClick={() => handleDelete(item)}>
                  <IconTrash />
                </ActionIcon>
              </Tooltip>
            ),
          },
        ]}
        records={data?.tournamentSteps}
        fetching={loading}
      />
    </div>
  );
};
